import React, { useCallback, useEffect, useRef, useState } from "react";
import JsBarcode from "jsbarcode";
import { api, getToken } from "../../../software/api";
import { toast } from "sonner";
import { X, Plus, Save, Trash2, Search, Loader2, ImageOff, Eraser, Printer, Barcode as BarcodeIcon } from "lucide-react";
import { imgUrl } from "../showroom/catalogShared";

const money = (v) => `$${Number(v || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export async function uploadImageRel(file) {
  const fd = new FormData(); fd.append("file", file);
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.detail || "Upload failed");
  return d.url;
}

export function ImageDrop({ value, onChange, label = "Image", tid = "ce-image" }) {
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const doFile = async (f) => {
    if (!f) return;
    if (!/^image\//.test(f.type) && !/\.(bmp|dib|tiff?)$/i.test(f.name)) { toast.error("Please drop an image (JPG, PNG, BMP…)"); return; }
    setBusy(true);
    try { onChange(await uploadImageRel(f)); toast.success("Image added"); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <label onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const u = e.dataTransfer.getData("text/anx-photo"); if (u) onChange(u); else doFile(e.dataTransfer.files?.[0]); }} data-testid={tid}
      className={`relative block w-full aspect-[4/3] bg-white border-2 rounded-md overflow-hidden cursor-pointer transition-colors ${over ? "border-emerald-500 bg-emerald-50" : "border-[#9db8d6] hover:border-[#1a4f8a]"}`}>
      <input type="file" accept="image/*,.bmp" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; doFile(f); }} data-testid={`${tid}-input`} />
      {value ? <img src={imgUrl(value)} alt={label} className="w-full h-full object-contain" />
        : <div className="h-full flex flex-col items-center justify-center text-slate-400 text-[12px] font-bold gap-1"><ImageOff className="h-8 w-8" />NO IMAGE<span>DRAG &amp; DROP</span><span className="font-normal text-[10px]">or click to choose (BMP ok)</span></div>}
      {busy && <div className="absolute inset-0 bg-white/70 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#1a4f8a]" /></div>}
      {value && <button type="button" onClick={(e) => { e.preventDefault(); onChange(""); }} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 text-rose-600 hover:bg-white" title="Remove image" data-testid={`${tid}-clear`}><X className="h-3.5 w-3.5" /></button>}
    </label>
  );
}

function FieldRow({ f, value, onChange, meta }) {
  const list = f.list && meta?.[f.list];
  return (
    <div className="grid grid-cols-[150px_1fr] items-center gap-1">
      <span className="text-right pr-1 font-bold text-slate-800 text-[13px]">{f.label}:</span>
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} list={list ? `ce-dl-${f.key}` : undefined} readOnly={f.readOnly}
        inputMode={f.money || f.num ? "decimal" : "text"} data-testid={`ce-f-${f.key}`}
        className={`w-full border border-[#9db8d6] px-2 py-1 text-[13px] ${f.money ? "text-right font-bold" : ""} ${f.readOnly ? "bg-slate-100" : "bg-white"} focus:outline-none focus:border-[#1a4f8a]`} />
      {list && <datalist id={`ce-dl-${f.key}`}>{list.map((x) => <option key={x} value={x} />)}</datalist>}
    </div>
  );
}

// ---- Parts extras: legacy markup buttons (Cost + x% then + 10% GST) and barcode ----
const MARKUPS = [0.5, 0.6, 0.7, 0.75, 0.8, 1, 1.1, 1.2];
export const retailFrom = (cost, m) => Math.round(Number(cost || 0) * (1 + m) * 1.1 * 100) / 100;

function printBarcode(rec) {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  JsBarcode(svg, String(rec.barcode), { format: "CODE128", displayValue: true, height: 50, fontSize: 13, margin: 4, width: 1.8 });
  const w = window.open("", "_blank");
  if (!w) { toast.error("Allow popups to print the barcode"); return; }
  w.document.write(`<html><head><title>Barcode ${rec.barcode}</title><style>body{font-family:Arial;margin:0;padding:10px;text-align:center}.d{font-size:12px;font-weight:700}.p{font-size:16px;font-weight:900;margin-top:2px}</style></head><body><div class="d">${(rec.description || "").replace(/</g, "&lt;")}</div>${svg.outerHTML}<div class="p">${money(rec.price)}</div></body></html>`);
  w.document.close(); w.focus(); w.print();
}

export function PartsExtras({ rec, set }) {
  return (
    <div className="space-y-2 mt-2">
      <div className="rounded-md border border-[#9db8d6] bg-white/70 p-2" data-testid="ce-markups">
        <div className="text-[11px] font-black text-[#123a63] mb-1">MARK-UP ON COST (then + 10% GST → Retail Price)</div>
        <div className="grid grid-cols-4 gap-1">
          {MARKUPS.map((m) => (
            <button key={m} onClick={() => set({ price: retailFrom(rec.cost_price, m) })} data-testid={`ce-markup-${Math.round(m * 100)}`}
              className="py-1.5 rounded-sm bg-[#1a4f8a] hover:bg-[#123a63] text-white text-[12px] font-black transition-transform hover:-translate-y-0.5">{Math.round(m * 100)}%</button>
          ))}
        </div>
      </div>
      <div className="rounded-md border border-[#9db8d6] bg-white/70 p-2 flex items-center gap-2" data-testid="ce-barcode-box">
        <BarcodeIcon className="h-5 w-5 text-[#123a63]" />
        <input value={rec.barcode || ""} onChange={(e) => set({ barcode: e.target.value })} placeholder="Barcode" data-testid="ce-f-barcode" className="flex-1 border border-[#9db8d6] px-2 py-1 text-[13px] font-mono" />
        <button onClick={() => set({ barcode: String(Math.floor(Math.random() * 1e11) + 1e6) })} data-testid="ce-barcode-generate" className="px-2 py-1 rounded-sm border border-[#1a4f8a] text-[#1a4f8a] text-[11px] font-black hover:bg-[#eaf1fb]">GENERATE</button>
        <button onClick={() => (rec.barcode ? printBarcode(rec) : toast.error("No barcode yet — click Generate"))} data-testid="ce-barcode-print" className="px-2 py-1 rounded-sm bg-[#123a63] text-white text-[11px] font-black hover:bg-[#0e2c4d] flex items-center gap-1"><Printer className="h-3.5 w-3.5" /> PRINT BARCODE</button>
      </div>
    </div>
  );
}

// Photo Tray: drop a batch of photos, then click/drag one onto the open record. Kept per browser until used.
export function PhotoTray({ kind, onUse, canUse }) {
  const key = `anx_photo_tray_${kind}`;
  const [photos, setPhotos] = useState(() => { try { return JSON.parse(localStorage.getItem(key) || "[]"); } catch { return []; } });
  const [busy, setBusy] = useState(0);
  const save = (list) => { setPhotos(list); localStorage.setItem(key, JSON.stringify(list)); };
  const addFiles = async (files) => {
    const imgs = [...files].filter((f) => /^image\//.test(f.type) || /\.(bmp|dib|tiff?)$/i.test(f.name));
    if (!imgs.length) return;
    setBusy(imgs.length);
    let list = [...photos];
    for (const f of imgs) {
      try { list = [...list, { url: await uploadImageRel(f), name: f.name }]; save(list); } catch (e) { toast.error(`${f.name}: ${e.message}`); }
      setBusy((b) => b - 1);
    }
  };
  const use = (p) => { if (!canUse) { toast.error("Open a record first"); return; } onUse(p.url); save(photos.filter((x) => x.url !== p.url)); };
  return (
    <div className="mt-3 rounded-md border-2 border-dashed border-[#9db8d6] bg-white/60 p-2" data-testid="ce-photo-tray"
      onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files); }}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-black text-[#123a63]">PHOTO TRAY ({photos.length})</span>
        <label className="text-[11px] font-bold text-[#1a4f8a] cursor-pointer hover:underline">+ Add photos<input type="file" multiple accept="image/*,.bmp" className="hidden" data-testid="ce-tray-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} /></label>
      </div>
      {busy > 0 && <div className="text-[11px] text-slate-500 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Uploading {busy}…</div>}
      {photos.length === 0 ? <p className="text-[11px] text-slate-400 text-center py-3">Drop a batch of photos here, then click one to attach it to the open record.</p> : (
        <div className="grid grid-cols-3 gap-1 max-h-56 overflow-y-auto">
          {photos.map((p) => (
            <div key={p.url} className="relative group">
              <button onClick={() => use(p)} draggable onDragStart={(e) => e.dataTransfer.setData("text/anx-photo", p.url)} title={`${p.name} — click to attach`} data-testid="ce-tray-photo"
                className="block w-full aspect-square rounded-sm overflow-hidden border border-slate-200 hover:ring-2 hover:ring-emerald-500"><img src={imgUrl(p.url)} alt={p.name} className="w-full h-full object-cover" /></button>
              <button onClick={() => save(photos.filter((x) => x.url !== p.url))} className="absolute top-0 right-0 bg-white/90 rounded-bl px-1 text-[10px] text-rose-600 opacity-0 group-hover:opacity-100" title="Remove from tray">✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RecordList({ cfg, meta, items, total, selId, search, setSearch, onPick, loading }) {
  return (
    <div className="w-72 shrink-0 flex flex-col border-r border-[#9db8d6] bg-white/60" data-testid="ce-list">
      <div className="p-2 space-y-1 border-b border-[#9db8d6]">
        {cfg.searches.map((s) => s.list ? (
          <select key={s.key} value={search[s.key] || ""} onChange={(e) => setSearch({ ...search, [s.key]: e.target.value })} data-testid={`ce-search-${s.key}`} className="w-full border border-[#9db8d6] px-2 py-1 text-[12px] bg-white">
            <option value="">{s.label} — all</option>{(meta?.[s.list] || []).map((x) => <option key={x}>{x}</option>)}
          </select>
        ) : (
          <div key={s.key} className="relative"><Search className="h-3.5 w-3.5 absolute left-2 top-2 text-slate-400" />
            <input value={search[s.key] || ""} onChange={(e) => setSearch({ ...search, [s.key]: e.target.value })} placeholder={s.label} data-testid={`ce-search-${s.key}`} className="w-full border border-[#9db8d6] pl-7 pr-2 py-1 text-[12px]" /></div>
        ))}
        <button onClick={() => setSearch({})} data-testid="ce-clear-searches" className="w-full py-1 rounded-sm border border-slate-300 text-slate-600 text-[11px] font-bold hover:bg-white flex items-center justify-center gap-1"><Eraser className="h-3.5 w-3.5" /> Clear Searches</button>
      </div>
      <div className="text-[10px] text-slate-500 px-2 py-0.5">{loading ? "Searching…" : `${total} record${total === 1 ? "" : "s"}${items.length < total ? ` · showing ${items.length}` : ""}`}</div>
      <div className="flex-1 overflow-y-auto">
        {items.map((r) => (
          <button key={r.id} onClick={() => onPick(r)} data-testid={`ce-row-${r.id}`}
            className={`w-full text-left px-2 py-1.5 border-b border-slate-100 text-[12px] transition-colors ${selId === r.id ? "bg-[#1a4f8a] text-white" : "hover:bg-[#eaf1fb]"}`}>
            <div className="font-bold truncate">{cfg.title(r) || "(untitled)"}</div>
            <div className={`text-[10px] truncate ${selId === r.id ? "text-sky-100" : "text-slate-500"}`}>{cfg.sub(r)}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function CatalogEditor({ cfg, onClose }) {
  const [meta, setMeta] = useState(null);
  const [search, setSearch] = useState({});
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [rec, setRec] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const firstLoad = useRef(true);

  useEffect(() => { api("GET", `/catalog-edit/${cfg.kind}/meta`).then(setMeta).catch(() => {}); }, [cfg.kind]);
  const load = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams(Object.entries(search).filter(([, v]) => v)).toString();
    try { setData(await api("GET", `/catalog-edit/${cfg.kind}?${qs}`)); } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [cfg.kind, search]);
  useEffect(() => { const t = setTimeout(load, firstLoad.current ? 0 : 300); firstLoad.current = false; return () => clearTimeout(t); }, [load]);

  const set = (patch) => { setRec((r) => ({ ...r, ...patch })); setDirty(true); };
  const save = async (quiet) => {
    if (!rec) return true;
    setBusy(true);
    try {
      const saved = rec.id ? await api("PATCH", `/catalog-edit/${cfg.kind}/${rec.id}`, rec) : await api("POST", `/catalog-edit/${cfg.kind}`, rec);
      setRec(saved); setDirty(false); load();
      if (!quiet) toast.success(`${cfg.noun} saved`);
      return true;
    } catch (e) { toast.error(e.message); return false; } finally { setBusy(false); }
  };
  const pick = async (r) => { if (dirty && !(await save(true))) return; setRec(r); setDirty(false); };
  const addNew = async () => { if (dirty && !(await save(true))) return; setRec({ ...cfg.blank }); setDirty(true); };
  const del = async () => {
    if (!rec?.id) { setRec(null); setDirty(false); return; }
    if (!window.confirm(`Are you sure you want to permanently delete this ${cfg.noun.toLowerCase()}?\n\n${cfg.title(rec)}`)) return;
    try { await api("DELETE", `/catalog-edit/${cfg.kind}/${rec.id}`); toast.success(`${cfg.noun} deleted`); setRec(null); setDirty(false); load(); } catch (e) { toast.error(e.message); }
  };
  const close = async () => { if (dirty && !(await save(true))) return; onClose(); };
  const onCost = cfg.kind === "parts" ? (v) => set({ cost_price: v, price: retailFrom(v, 0.8) }) : null;

  return (
    <div className="fixed inset-0 z-[66] bg-slate-900/60 flex items-center justify-center p-3" data-testid={`ce-${cfg.kind}`}>
      <div className="w-full max-w-[1500px] h-[90vh] bg-[#eaf1fb] border-2 border-[#1a4f8a] rounded-lg shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center gap-2 bg-[#1a4f8a] px-3 py-2">
          <button onClick={addNew} data-testid="ce-add" className="rounded bg-white px-3 py-1.5 text-sm font-black text-[#1a4f8a] hover:bg-slate-100 flex items-center gap-1.5"><Plus className="h-4 w-4" /> {cfg.addLabel}</button>
          <span className="text-white font-black text-[15px] ml-2">{cfg.heading}</span>
          <div className="ml-auto flex gap-2">
            {rec && <button onClick={del} data-testid="ce-delete" className="rounded bg-red-600 px-3 py-1.5 text-sm font-bold text-white hover:bg-red-700 flex items-center gap-1.5"><Trash2 className="h-4 w-4" /> Delete</button>}
            {rec && <button onClick={() => save(false)} disabled={busy || !dirty} data-testid="ce-save" className="rounded bg-emerald-600 px-3 py-1.5 text-sm font-black text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {dirty ? "Save" : "Saved"}</button>}
            <button onClick={close} data-testid="ce-close" className="rounded bg-white/90 px-3 py-1.5 text-sm font-bold text-[#1a4f8a] hover:bg-white flex items-center gap-1.5"><X className="h-4 w-4" /> Close Form</button>
          </div>
        </div>
        <div className="flex flex-1 min-h-0">
          <RecordList cfg={cfg} meta={meta} items={data.items} total={data.total} selId={rec?.id} search={search} setSearch={setSearch} onPick={pick} loading={loading} />
          {!rec ? (
            <div className="flex-1 grid place-items-center text-slate-500 text-sm" data-testid="ce-empty">Pick a record on the left, or click <b className="mx-1">{cfg.addLabel}</b>.</div>
          ) : (
            <div className="flex-1 overflow-y-auto p-4 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_17rem] gap-4" data-testid="ce-form">
              {cfg.columns.map((col, ci) => (
                <div key={ci} className="space-y-1">
                  {col.map((f) => <FieldRow key={f.key} f={f} meta={meta} value={rec[f.key]} onChange={(v) => (f.key === "cost_price" && onCost ? onCost(v) : set({ [f.key]: v }))} />)}
                  {ci === 1 && cfg.kind === "parts" && <PartsExtras rec={rec} set={set} />}
                </div>
              ))}
              <div>
                <p className="text-[11px] font-black text-[#123a63] mb-1">{cfg.noun} Image</p>
                <ImageDrop value={rec.image_url} onChange={(u) => set({ image_url: u })} label={cfg.noun} />
                <PhotoTray kind={cfg.kind} canUse onUse={(u) => set({ image_url: u })} />
                {cfg.kind === "parts" && <div className="mt-2 text-[12px] text-slate-600">Retail: <b className="text-emerald-700">{money(rec.price)}</b> · Cost: <b>{money(rec.cost_price)}</b></div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
